/**
 * The colors utilities
 */
export default class Colors {
    /**
 * Shifts the hue of a color by a given percentage and returns the new color in HEX format.
 * 
 * @param {string} hex - The input color in HEX format (e.g., "#ff0000").
 * @param {number} percentage - The percentage to shift the hue. Positive values shift clockwise, negative values shift counterclockwise.
 * @returns {string} - The new color in HEX format after shifting the hue.
 * 
 * @example
 * shiftHue("#ff0000", 30); // Shifts red hue by 30%
 */
public static shiftHue(hex: string, percentage: number): string {
    // Convert HEX to HSL
    global.executions.addLine({className: "Colors",functionName: "shiftHue",hierarchy: ["/shared/utils/colors.ts", "Colors", "shiftHue"],fileName: "/shared/utils/colors.ts",childLine: 17,line: "    const hsl = this.hexToHsl(hex);"});
    const hsl = this.hexToHsl(hex);
  
    // Shift the hue
    global.executions.addLine({className: "Colors",functionName: "shiftHue",hierarchy: ["/shared/utils/colors.ts", "Colors", "shiftHue"],fileName: "/shared/utils/colors.ts",childLine: 20,line: "    hsl.h = (hsl.h + percentage) % 360;"});
    hsl.h = (hsl.h + percentage) % 360;
    if (hsl.h < 0) {
      global.executions.addLine({className: "Colors",functionName: "shiftHue",hierarchy: ["/shared/utils/colors.ts", "Colors", "shiftHue"],fileName: "/shared/utils/colors.ts",childLine: 22,line: "      hsl.h += 360;"});
      hsl.h += 360;
    }
  
    // Convert the new HSL back to HEX
    global.executions.addLine({className: "Colors",functionName: "shiftHue",hierarchy: ["/shared/utils/colors.ts", "Colors", "shiftHue"],fileName: "/shared/utils/colors.ts",childLine: 26,line: "    return this.hslToHex(hsl.h, hsl.s, hsl.l);"});
    return this.hslToHex(hsl.h, hsl.s, hsl.l);
  }
  
  /**
   * Converts HEX color to HSL format.
   * 
   * @param {string} hex - The color in HEX format (e.g., "#ff0000").
   * @returns {Object} - The color in HSL format { h: number, s: number, l: number }.
   */
  public static hexToHsl(hex: string): { h: number, s: number, l: number } {
    // Remove the leading "#" if it exists
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 37,line: "    hex = hex.replace(/^#/, '');"});
    hex = hex.replace(/^#/, '');
  
    // Parse the red, green, and blue components
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 40,line: "    let r = parseInt(hex.substring(0, 2), 16) / 255;"});
    let r = parseInt(hex.substring(0, 2), 16) / 255;
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 41,line: "    let g = parseInt(hex.substring(2, 4), 16) / 255;"});
    let g = parseInt(hex.substring(2, 4), 16) / 255;
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 42,line: "    let b = parseInt(hex.substring(4, 6), 16) / 255;"});
    let b = parseInt(hex.substring(4, 6), 16) / 255;
  
    // Find the minimum and maximum RGB values
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 45,line: "    let min = Math.min(r, g, b);"});
    let min = Math.min(r, g, b);
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 46,line: "    let max = Math.max(r, g, b);"});
    let max = Math.max(r, g, b);
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 47,line: "    let delta = max - min;"});
    let delta = max - min;
  
    // Calculate lightness
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 50,line: "    let l = (max + min) / 2;"});
    let l = (max + min) / 2;
  
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 52,line: "    let h = 0;"});
    let h = 0;
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 53,line: "    let s = 0;"});
    let s = 0;
  
    if (delta !== 0) {
      // Calculate saturation
      global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 57,line: "      s = (l > 0.5) ? delta / (2 - max - min) : delta / (max + min);"});
      s = (l > 0.5) ? delta / (2 - max - min) : delta / (max + min);
  
      // Calculate hue
      switch (max) {
        case r:
          h = (g - b) / delta + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / delta + 2;
          break;
        case b:
          h = (r - g) / delta + 4;
          break;
      }
      global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 71,line: "      h *= 60;"});
      h *= 60;
    }
  
    global.executions.addLine({className: "Colors",functionName: "hexToHsl",hierarchy: ["/shared/utils/colors.ts", "Colors", "hexToHsl"],fileName: "/shared/utils/colors.ts",childLine: 74,line: "    return { h: h, s: s * 100, l: l * 100 };"});
    return { h: h, s: s * 100, l: l * 100 };
  }
  
  /**
   * Converts HSL color to HEX format.
   * 
   * @param {number} h - The hue of the color (0 to 360).
   * @param {number} s - The saturation of the color (0 to 100).
   * @param {number} l - The lightness of the color (0 to 100).
   * @returns {string} - The color in HEX format.
   */
  public static hslToHex(h: number, s: number, l: number): string {
    // Normalize the saturation and lightness to [0, 1]
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 87,line: "    s /= 100;"});
    s /= 100;
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 88,line: "    l /= 100;"});
    l /= 100;
  
    // Calculate the chroma
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 91,line: "    const c = (1 - Math.abs(2 * l - 1)) * s;"});
    const c = (1 - Math.abs(2 * l - 1)) * s;
  
    // Calculate the hue prime (h')
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 94,line: "    const hPrime = h / 60;"});
    const hPrime = h / 60;
  
    // Calculate the x component of RGB
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 97,line: "    const x = c * (1 - Math.abs((hPrime % 2) - 1));"});
    const x = c * (1 - Math.abs((hPrime % 2) - 1));
  
    // Determine the RGB values
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 100,line: "    let r = 0, g = 0, b = 0;"});
    let r = 0, g = 0, b = 0;
  
    if (0 <= hPrime && hPrime < 1) {
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 103,line: "      r = c;"});
      r = c;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 104,line: "      g = x;"});
      g = x;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 105,line: "      b = 0;"});
      b = 0;
    } else if (1 <= hPrime && hPrime < 2) {
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 107,line: "      r = x;"});
      r = x;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 108,line: "      g = c;"});
      g = c;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 109,line: "      b = 0;"});
      b = 0;
    } else if (2 <= hPrime && hPrime < 3) {
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 111,line: "      r = 0;"});
      r = 0;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 112,line: "      g = c;"});
      g = c;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 113,line: "      b = x;"});
      b = x;
    } else if (3 <= hPrime && hPrime < 4) {
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 115,line: "      r = 0;"});
      r = 0;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 116,line: "      g = x;"});
      g = x;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 117,line: "      b = c;"});
      b = c;
    } else if (4 <= hPrime && hPrime < 5) {
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 119,line: "      r = x;"});
      r = x;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 120,line: "      g = 0;"});
      g = 0;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 121,line: "      b = c;"});
      b = c;
    } else if (5 <= hPrime && hPrime < 6) {
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 123,line: "      r = c;"});
      r = c;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 124,line: "      g = 0;"});
      g = 0;
      global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 125,line: "      b = x;"});
      b = x;
    }
  
    // Add the lightness component (m) to adjust for the color shift
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 129,line: "    const m = l - c / 2;"});
    const m = l - c / 2;
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 130,line: "    r += m;"});
    r += m;
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 131,line: "    g += m;"});
    g += m;
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 132,line: "    b += m;"});
    b += m;
  
    // Convert RGB to HEX format
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 135,line: "    r = Math.round(r * 255);"});
    r = Math.round(r * 255);
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 136,line: "    g = Math.round(g * 255);"});
    g = Math.round(g * 255);
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 137,line: "    b = Math.round(b * 255);"});
    b = Math.round(b * 255);
  
    // Return the color in HEX format
    global.executions.addLine({className: "Colors",functionName: "hslToHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "hslToHex"],fileName: "/shared/utils/colors.ts",childLine: 140,line: "    return `#${this.toHex(r)}${this.toHex(g)}${this.toHex(b)}`;"});
    return `#${this.toHex(r)}${this.toHex(g)}${this.toHex(b)}`;
  }
  
  /**
   * Converts a number to a two-digit hexadecimal string.
   * 
   * @param {number} value - The numeric value to convert.
   * @returns {string} - The two-digit hexadecimal representation of the value.
   */
  public static toHex(value: number): string {
    global.executions.addLine({className: "Colors",functionName: "toHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "toHex"],fileName: "/shared/utils/colors.ts",childLine: 150,line: "    const hex = value.toString(16);"});
    const hex = value.toString(16);
    global.executions.addLine({className: "Colors",functionName: "toHex",hierarchy: ["/shared/utils/colors.ts", "Colors", "toHex"],fileName: "/shared/utils/colors.ts",childLine: 151,line: "    return hex.length === 1 ? `0${hex}` : hex;"});
    return hex.length === 1 ? `0${hex}` : hex;
  }
  
}